// ============================================================================
// "Applies to" section of the create-coupon modal: brand, category and product
// rules. Each rule is All / Only these / All except, mapped onto the coupon's
// list + is_*_excluded flag exactly as pricing reads it (CouponCode.is_item_eligible):
//   All         -> excluded, empty list (no restriction)
//   Only these  -> not excluded, list
//   All except  -> excluded, list
// When more than one rule is restricted, an item qualifies if it passes ANY rule.
// ============================================================================
import { Alert, Button, Form, Input, message, Radio, Select, Spin, Tag } from "antd";
import React, { useRef, useState } from "react";
import {
  CouponScopeOption,
  CouponTemplateDetail,
  CouponVariantRef,
  resolveCouponVariants,
  searchCouponScopeOptions,
} from "../../../apis/client.api";

export type ScopeMode = "all" | "only" | "except";

// labelInValue entries from the brand / category selects.
type Labeled = { value: number; label: React.ReactNode };

const MODE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "only", label: "Only these" },
  { value: "except", label: "All except" },
];

const modeFrom = (excluded: boolean, count: number): ScopeMode =>
  !excluded ? "only" : count > 0 ? "except" : "all";

// Form values for the scope section, pre-filled from a template coupon.
export const scopeFromTemplate = (t: CouponTemplateDetail) => ({
  brand_mode: modeFrom(t.is_brand_excluded, t.brands.length),
  brand_ids: t.brands.map((b) => ({ value: b.id, label: b.name || `#${b.id}` })),
  category_mode: modeFrom(t.is_category_excluded, t.categories.length),
  category_ids: t.categories.map((c) => ({ value: c.id, label: c.name || `#${c.id}` })),
  variant_mode: modeFrom(t.is_variant_excluded, t.variants.length),
  variant_items: t.variants.map((v) => ({ id: v.id, name: v.name, handle: v.handle })),
});

export const SCOPE_DEFAULTS = {
  brand_mode: "all",
  brand_ids: [],
  category_mode: "all",
  category_ids: [],
  variant_mode: "all",
  variant_items: [],
};

export interface ScopeBody {
  is_brand_excluded: boolean;
  brand_ids: number[];
  is_category_excluded: boolean;
  category_ids: number[];
  is_variant_excluded: boolean;
  variant_ids: number[];
}

// What the review step shows: the mode plus the names, per rule.
export interface ScopeSummaryData {
  brands: { mode: ScopeMode; names: string[] };
  categories: { mode: ScopeMode; names: string[] };
  products: { mode: ScopeMode; names: string[] };
}

const rule = (mode: ScopeMode, ids: number[]) =>
  mode === "all" ? { excluded: true, ids: [] as number[] } : { excluded: mode === "except", ids };

const labelText = (l: Labeled) => (typeof l.label === "string" ? l.label : `#${l.value}`);

// Pulls the scope keys out of the validated form values.
export const scopeToBody = (values: Record<string, any>): { body: ScopeBody; summary: ScopeSummaryData } => {
  const brands: Labeled[] = values.brand_ids ?? [];
  const categories: Labeled[] = values.category_ids ?? [];
  const products: CouponVariantRef[] = values.variant_items ?? [];
  const b = rule(values.brand_mode, brands.map((x) => x.value));
  const c = rule(values.category_mode, categories.map((x) => x.value));
  const v = rule(values.variant_mode, products.map((x) => x.id));
  return {
    body: {
      is_brand_excluded: b.excluded,
      brand_ids: b.ids,
      is_category_excluded: c.excluded,
      category_ids: c.ids,
      is_variant_excluded: v.excluded,
      variant_ids: v.ids,
    },
    summary: {
      brands: { mode: values.brand_mode, names: values.brand_mode === "all" ? [] : brands.map(labelText) },
      categories: {
        mode: values.category_mode,
        names: values.category_mode === "all" ? [] : categories.map(labelText),
      },
      products: {
        mode: values.variant_mode,
        names: values.variant_mode === "all" ? [] : products.map((p) => `${p.name} (${p.handle})`),
      },
    },
  };
};

export const SCOPE_KEYS = [
  "brand_mode",
  "brand_ids",
  "category_mode",
  "category_ids",
  "variant_mode",
  "variant_items",
];

const TagList: React.FC<{ names: string[] }> = ({ names }) => (
  <div style={{ maxHeight: 120, overflowY: "auto" }}>
    {names.map((n, i) => (
      <Tag key={i} style={{ marginBottom: 4 }}>
        {n}
      </Tag>
    ))}
  </div>
);

const describeRule = (mode: ScopeMode, names: string[], noun: string) =>
  mode === "all" ? (
    `All ${noun}`
  ) : (
    <>
      <div className="text-muted">
        {mode === "only" ? `Only these ${noun}` : `All ${noun} except`} ({names.length}):
      </div>
      <TagList names={names} />
    </>
  );

export const restrictedRuleCount = (s: ScopeSummaryData) =>
  [s.brands, s.categories, s.products].filter((r) => r.mode !== "all").length;

export const AnyRuleNote = () => (
  <Alert
    type="info"
    showIcon
    style={{ marginBottom: 12 }}
    message="More than one rule is set: an item gets the discount if it matches ANY one of them, not all."
  />
);

// Review-step rows.
export const scopeRows = (s: ScopeSummaryData) => [
  { label: "Brands", value: describeRule(s.brands.mode, s.brands.names, "brands") },
  { label: "Categories", value: describeRule(s.categories.mode, s.categories.names, "categories") },
  { label: "Products", value: describeRule(s.products.mode, s.products.names, "products") },
];

// --- inputs ----------------------------------------------------------------

const RemoteMultiSelect: React.FC<{
  kind: "brand" | "category";
  value?: Labeled[];
  onChange?: (v: Labeled[]) => void;
}> = ({ kind, value, onChange }) => {
  const [options, setOptions] = useState<CouponScopeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const search = (q: string) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const mine = ++seq.current;
      setLoading(true);
      const rows = await searchCouponScopeOptions(kind, q);
      if (mine === seq.current) {
        setOptions(rows);
        setLoading(false);
      }
    }, 300);
  };

  return (
    <Select
      mode="multiple"
      labelInValue
      showSearch
      filterOption={false}
      style={{ width: "100%" }}
      placeholder={kind === "brand" ? "Search brands" : "Search categories"}
      value={value}
      onChange={(v) => onChange?.(v as Labeled[])}
      onSearch={search}
      onDropdownVisibleChange={(o) => o && options.length === 0 && search("")}
      notFoundContent={loading ? <Spin size="small" /> : "Nothing found"}
      options={options.map((o) => ({ value: o.id, label: o.name }))}
    />
  );
};

// Paste ids / handles / product URLs, resolve them, keep the matched products.
const ProductPasteInput: React.FC<{
  value?: CouponVariantRef[];
  onChange?: (v: CouponVariantRef[]) => void;
}> = ({ value = [], onChange }) => {
  const [text, setText] = useState("");
  const [resolving, setResolving] = useState(false);
  const [notFound, setNotFound] = useState<string[]>([]);

  const add = async () => {
    if (!text.trim()) return;
    setResolving(true);
    try {
      const { found, not_found } = await resolveCouponVariants(text);
      const have = new Set(value.map((v) => v.id));
      const added = found.filter((f) => !have.has(f.id));
      onChange?.([...value, ...added]);
      setNotFound(not_found);
      setText(not_found.join("\n"));
      message.success(`${added.length} product${added.length === 1 ? "" : "s"} added`);
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Could not look up products");
    } finally {
      setResolving(false);
    }
  };

  return (
    <div>
      <Input.TextArea
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste variant handles, ids or product links — one per line or comma-separated"
      />
      <div className="d-flex justify-content-between align-items-center" style={{ marginTop: 8 }}>
        <span className="text-muted">{value.length} selected</span>
        <span>
          {value.length > 0 && (
            <Button type="link" size="small" danger onClick={() => onChange?.([])}>
              Clear all
            </Button>
          )}
          <Button size="small" onClick={add} loading={resolving} disabled={!text.trim()}>
            Add products
          </Button>
        </span>
      </div>
      {notFound.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message={`Not found (${notFound.length}) — left in the box to fix: ${notFound.slice(0, 10).join(", ")}${
            notFound.length > 10 ? "…" : ""
          }`}
        />
      )}
      {value.length > 0 && (
        <div style={{ maxHeight: 160, overflowY: "auto", marginTop: 8 }}>
          {value.map((v) => (
            <Tag
              key={v.id}
              closable
              style={{ marginBottom: 4 }}
              onClose={(e) => {
                e.preventDefault();
                onChange?.(value.filter((x) => x.id !== v.id));
              }}
            >
              {v.name} <span className="text-muted">({v.handle})</span>
            </Tag>
          ))}
        </div>
      )}
    </div>
  );
};

const requiredUnlessAll = (modeField: string, noun: string) => ({ getFieldValue }: any) => ({
  validator: (_: unknown, v: unknown[]) =>
    getFieldValue(modeField) === "all" || (v && v.length > 0)
      ? Promise.resolve()
      : Promise.reject(`Pick at least one ${noun}`),
});

const ScopeRule: React.FC<{
  label: string;
  modeName: string;
  listName: string;
  noun: string;
  children: React.ReactNode;
}> = ({ label, modeName, listName, noun, children }) => {
  const form = Form.useFormInstance();
  const mode = Form.useWatch(modeName, form);
  return (
    <>
      <Form.Item label={label} name={modeName} style={{ marginBottom: mode === "all" ? 24 : 8 }}>
        <Radio.Group
          optionType="button"
          options={MODE_OPTIONS}
          onChange={() => form.validateFields([listName]).catch(() => undefined)}
        />
      </Form.Item>
      {/* Kept mounted (hidden) under "All" so switching back doesn't lose the picks. */}
      <Form.Item
        name={listName}
        hidden={mode === "all"}
        dependencies={[modeName]}
        rules={[requiredUnlessAll(modeName, noun)]}
      >
        {children}
      </Form.Item>
    </>
  );
};

export const ScopeFields: React.FC = () => (
  <>
    <div className="th-font-weight-600" style={{ margin: "8px 0 12px" }}>
      Applies to
    </div>
    <ScopeRule label="Brands" modeName="brand_mode" listName="brand_ids" noun="brand">
      <RemoteMultiSelect kind="brand" />
    </ScopeRule>
    <ScopeRule label="Categories" modeName="category_mode" listName="category_ids" noun="category">
      <RemoteMultiSelect kind="category" />
    </ScopeRule>
    <ScopeRule label="Products" modeName="variant_mode" listName="variant_items" noun="product">
      <ProductPasteInput />
    </ScopeRule>
  </>
);
